import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useEffect, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import DataConnector from 'connect/DataConnector';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ModelHubBulkBanner from 'model-hub/ModelHubBulkBanner';
import StructurePredictionForm from 'model-hub/scientific-models/input-forms/structure-prediction/StructurePredictionForm';
import {
  requireScientificModel,
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {
  CoordinateStructuresList,
  InProgressPredictionData,
} from 'model-hub/scientific-models/structure-prediction/CoordinateStructureList';
import {
  AvailableStructureInfo,
  getRunDisabledMessage,
  getCoordinateStructureId,
} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import {
  useStructurePredictionViewState,
  ViewState,
} from 'model-hub/scientific-models/structure-prediction/structurePredictionViewState';
import {StructureViewerPane} from 'model-hub/scientific-models/structure-prediction/StructureViewerPane';
import {useRunAllModels} from 'model-hub/scientific-models/structure-prediction/useRunAllModels';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {ProteinVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import notifications from 'notifications';
import {aaSequenceSelectionUIGlobalState} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import legacyRunModal from 'ui-library/legacyRunModal';

import {
  HasInProgressStructurePredictionJobsQuery,
  HasInProgressStructurePredictionJobsQueryVariables,
} from './__generated/HasInProgressStructurePredictionJobsQuery';
import {
  findCoordinateStructureById,
  getStructuresForVersion,
  getStructureInfoFromVersions,
} from './AlphaFoldTabUtils';
import RedoPredictionModal from './RedoPredictionModal';

export const HAS_IN_PROGRESS_STRUCTURE_PREDICTION_JOBS: DocumentNode = gql`
  query HasInProgressStructurePredictionJobsQuery($proteinId: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    aaSequence(id: $proteinId) {
      ... on ProteinNode {
        id
        inProgressStructurePredictionJobs {
          id
          statusCode
          modelName
          createdAt
          componentApiIds
        }
      }
      ... on AaSequenceNode {
        id
        inProgressStructurePredictionJobs {
          id
          statusCode
          modelName
          createdAt
          componentApiIds
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AlphaFoldTabContentProps =
  | {
      aaSequenceId: string;
      initialView?: undefined;
      clearInitialView?: undefined;
    }
  | {
      aaSequenceId: string;
      initialView: ViewState;
      clearInitialView: () => void;
    };

interface AlphaFoldTabContentEnhancedProps {
  versions?: Array<ProteinVersion>;
  currentlyViewedAaSequenceVersionId: number;
  aminoAcids: string;
  permissions: Permissions;
  currentCoordinateStructureId: string | null;
  folderId: string;
}

class AlphaFoldTabContentConnector extends DataConnector<
  AlphaFoldTabContentProps,
  AlphaFoldTabContentEnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore];

  enhanceProps(): AlphaFoldTabContentEnhancedProps {
    const aaSequenceStore = this.aaSequenceStore();
    const aaSequence = aaSequenceStore.getAaSequenceById(this.props.aaSequenceId)!;
    const currentlyViewedAaSequenceVersionId = aaSequence.version.id;
    return {
      versions: aaSequenceStore.getVersionsById(this.props.aaSequenceId),
      currentlyViewedAaSequenceVersionId,
      aminoAcids: aaSequence.version.aminoAcids,
      permissions: aaSequence.permissions,
      currentCoordinateStructureId: aaSequenceStore.getCoordinateStructureId(
        currentlyViewedAaSequenceVersionId
      ),
      folderId: aaSequence.folder.api_identifier,
    };
  }
}

function AlphaFoldTab({...props}: AlphaFoldTabContentProps & AlphaFoldTabContentEnhancedProps): JSX.Element {
  assert(
    props.initialView == null || props.clearInitialView != null,
    'clearInitialView must be provided when initialView is set to prevent component from resetting to initial view on every re-render'
  );
  const result = useScientificModelRegistry();
  const registryError = result.type === 'error' ? result.error : null;
  useEffect(() => {
    if (registryError != null) {
      notifications.error('Error when loading scientific models. Please refresh the page.');
    }
  }, [registryError]);

  if (result.type === 'loading') {
    return <CenteredHorizontalThrobber />;
  }
  if (result.type === 'error') {
    return <></>;
  }
  return (
    <LoadedAlphaFoldTab
      {...props}
      availableModels={result.registry.availableStructurePredictionModels}
      catalogByExternalReference={result.registry.catalogByExternalReference}
    />
  );
}

function LoadedAlphaFoldTab({
  availableModels,
  catalogByExternalReference,
  aaSequenceId,
  initialView,
  clearInitialView,
  versions,
  currentlyViewedAaSequenceVersionId,
  aminoAcids,
  permissions,
  currentCoordinateStructureId,
  folderId,
}: AlphaFoldTabContentProps &
  AlphaFoldTabContentEnhancedProps & {
    availableModels: ReadonlyArray<ScientificModelNode>;
    catalogByExternalReference: ScientificModelsByExternalReference;
  }): JSX.Element {
  const [aaSequenceSelectionUIState] = aaSequenceSelectionUIGlobalState.useGlobalClientState(aaSequenceId);
  const {start: selectionStart, end: selectionEnd} = aaSequenceSelectionUIState;
  const structureInfo = getStructureInfoFromVersions(currentlyViewedAaSequenceVersionId, versions);

  const structuresForVersion: Array<AvailableStructureInfo> = useMemo(
    () => getStructuresForVersion(versions, structureInfo.versionId || undefined),
    [versions, structureInfo.versionId]
  );

  const selectedCoordinateStructure = currentCoordinateStructureId
    ? findCoordinateStructureById(structuresForVersion, currentCoordinateStructureId)
    : null;

  const batchQueryResult = useBenchlingQuery<
    HasInProgressStructurePredictionJobsQuery,
    HasInProgressStructurePredictionJobsQueryVariables
  >(HAS_IN_PROGRESS_STRUCTURE_PREDICTION_JOBS, {
    variables: {proteinId: aaSequenceId},
  });

  const batchQueryData = batchQueryResult.type === QueryResultType.SUCCESS ? batchQueryResult.data : null;

  const inProgressStructurePredictions: Array<InProgressPredictionData> = useMemo(() => {
    if (!batchQueryData || isInaccessible(batchQueryData.aaSequence)) {
      return [];
    }
    return (batchQueryData.aaSequence.inProgressStructurePredictionJobs ?? []).map((job) => ({
      id: job.id,
      model: requireScientificModel(catalogByExternalReference, job.modelName),
      createdAt: job.createdAt,
      componentIds: job.componentApiIds,
    }));
  }, [batchQueryData, catalogByExternalReference]);

  const hasPredictionInProgress = inProgressStructurePredictions.length > 0;

  const {viewState, setViewState} = useStructurePredictionViewState({
    structurePredictionQueryStatus: batchQueryResult.type,
    hasCompletedStructures: structuresForVersion.length > 0,
    hasInProgressPredictions: hasPredictionInProgress,
    initialView,
  });

  // Clear initialView from tab state after consuming it once
  useEffect(() => {
    if (initialView != null && clearInitialView) {
      clearInitialView();
    }
    // Only run once on mount when initialView is present
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear current coordinate structure when switching to coordinate structures list view
  useEffect(() => {
    if (viewState === ViewState.COORDINATE_STRUCTURES_LIST) {
      AaSequenceActions.setCoordinateStructureId(currentlyViewedAaSequenceVersionId, null);
    }
  }, [viewState, currentlyViewedAaSequenceVersionId]);

  const {handleRunAllModels, submitting: runAllModelsSubmitting} = useRunAllModels({
    models: availableModels,
    isListView: viewState === ViewState.COORDINATE_STRUCTURES_LIST,
    inputComponent: {id: aaSequenceId, folderId, permissions, aminoAcids},
    onSubmitRunAllModels: () => {
      batchQueryResult.refetch();
    },
    versionId: currentlyViewedAaSequenceVersionId,
  });

  // Render based on viewType
  switch (viewState) {
    case ViewState.LOADING:
      return <CenteredHorizontalThrobber />;

    case ViewState.ERROR:
      notifications.error('Error loading structure prediction details. Please refresh the page.');
      return <></>;

    case ViewState.COORDINATE_STRUCTURES_LIST: {
      const disabledMessage = getRunDisabledMessage({
        hasAvailableModels: availableModels.length > 0,
        structurePredictionEditable: permissions.structurePredictionEditable === true,
      });

      return (
        <>
          <ModelHubBulkBanner />
          <CoordinateStructuresList
            coordinateStructures={structuresForVersion}
            onSelectCoordinateStructure={(coordinateStructure) => {
              const coordinateStructureId = getCoordinateStructureId(coordinateStructure);
              AaSequenceActions.setCoordinateStructureId(
                currentlyViewedAaSequenceVersionId,
                coordinateStructureId
              );
              setViewState(ViewState.VIEWER);
            }}
            onCreateRun={() => {
              setViewState(ViewState.RUN_STRUCTURE_PREDICTION);
            }}
            onRunAllModels={handleRunAllModels}
            inProgressStructurePredictions={inProgressStructurePredictions}
            disabledMessage={disabledMessage}
            runAllModelsDisabled={runAllModelsSubmitting}
            catalogByExternalReference={catalogByExternalReference}
          />
        </>
      );
    }

    case ViewState.VIEWER: {
      // Use selected coordinate structure if available, otherwise fall back to structureInfo
      const viewerCoordinateStructure =
        selectedCoordinateStructure ||
        (structureInfo.structureFile
          ? {
              structureFile: structureInfo.structureFile,
              model: structureInfo.model,
              modelVersion: structureInfo.modelVersion,
              versionId: structureInfo.versionId,
              createdAt: structureInfo.createdAt,
              structureFileOutOfDate: structureInfo.structureFileOutOfDate || false,
              coordinateStructureId: structureInfo.coordinateStructureId,
              componentIds: structureInfo.componentIds,
            }
          : null);

      if (!viewerCoordinateStructure) {
        // Fallback to loading if somehow we get into an inconsistent state
        return <CenteredHorizontalThrobber />;
      }

      const model = requireScientificModel(catalogByExternalReference, viewerCoordinateStructure.model);
      return (
        <StructureViewerPane
          structureInfo={viewerCoordinateStructure}
          catalogByExternalReference={catalogByExternalReference}
          predictionInProgress={hasPredictionInProgress}
          primaryEntityId={aaSequenceId}
          aminoAcids={aminoAcids}
          permissions={permissions}
          currentAaSequenceSequenceAminoAcids={aminoAcids}
          selectionStart={selectionStart}
          selectionEnd={selectionEnd}
          interactionAnalyticsEvent={MOLBIO_ANALYTICS_ACTIONS.INTERACTED_ALPHAFOLD_VIEWER}
          testComponent="AlphaFoldTabView"
          redoPrediction={() => {
            trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLICKED_ALPHAFOLD_REDO_RUN);
            legacyRunModal(RedoPredictionModal, {
              availableModels,
              aaSequenceId,
              folderId,
              previouslyRanModel: model,
            });
          }}
          onBack={() => {
            setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
          }}
        />
      );
    }

    case ViewState.RUN_STRUCTURE_PREDICTION:
      return (
        <>
          <ModelHubBulkBanner />
          <StructurePredictionForm
            availableModels={availableModels}
            inputComponent={{id: aaSequenceId, folderId, permissions, aminoAcids}}
            onSubmit={() => {
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
            onBack={() => {
              setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
            }}
            onRunAllModels={handleRunAllModels}
            source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
          />
        </>
      );
    default:
      return <CenteredHorizontalThrobber />;
  }
}

const ConnectedAlphaFoldContent = AlphaFoldTabContentConnector.connect(AlphaFoldTab);
export default ConnectedAlphaFoldContent;
